import { Menu, MenuContent, MenuRadioGroup, MenuRadioItem, MenuTrigger } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './agent-switcher.module.css'

export interface AgentSwitcherRow {
  id: string
  /** What the band calls it */
  name: string
  /** The name with what it's for, where the role isn't on screen: "Nils, Lyric Coach" */
  accessibleName: string
  /** What the agent is for, in a line */
  purpose: string
  /** Activity newer than this user last saw */
  unread: boolean
}

export interface AgentSwitcherProps {
  rows: AgentSwitcherRow[]
  activeId: string
  onSelect: (agentId: string) => void
}

/** C9a: the dot's accessible name */
const newMessages = (row: AgentSwitcherRow) => `${row.accessibleName}, new messages`

const UnreadDot: FC<{ row: AgentSwitcherRow }> = ({ row }) => (
  <span className={styles.dot} role="img" aria-label={newMessages(row)} />
)

/**
 * One agent switcher in the Agents panel header (chat makeover #2718). It replaces the per-agent tabs, which don't
 * fit three agents in 420px. It shows the current agent's name and purpose, and lists the others with their purpose
 * and a dot for new messages (C1, C6, C9a; C9's count is retired, since unread is a yes/no).
 */
export const AgentSwitcher: FC<AgentSwitcherProps> = props => {
  const { rows, activeId, onSelect } = props
  const active = rows.find(row => row.id === activeId) ?? rows[0]
  if (!active) {
    return null
  }
  const othersUnread = rows.some(row => row.id !== active.id && row.unread)

  return (
    <div className={styles.switcher}>
      <div className={styles.current}>
        <span className={styles.name}>
          {active.name}
          {active.unread ? <UnreadDot row={active} /> : null}
        </span>
        <span className={styles.purpose}>{active.purpose}</span>
        <span className={styles.count}>{`${rows.length} agents on this song`}</span>
      </div>
      <Menu>
        <MenuTrigger
          render={
            <button type="button" className={styles.trigger} aria-label="Switch agent">
              <span aria-hidden="true">⌄</span>
              {othersUnread ? <span className={styles.dot} data-testid="switcher-unread" aria-hidden="true" /> : null}
            </button>
          }
        />
        <MenuContent>
          <MenuRadioGroup value={active.id} onValueChange={value => onSelect(String(value))}>
            {rows.map(row => (
              <MenuRadioItem key={row.id} value={row.id} aria-label={row.accessibleName}>
                <span className={styles.row}>
                  <span className={styles.name}>
                    {row.name}
                    {row.unread ? <UnreadDot row={row} /> : null}
                  </span>
                  <span className={styles.purpose}>{row.purpose}</span>
                </span>
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MenuContent>
      </Menu>
    </div>
  )
}
